import type { ReactNode } from 'react';

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-12 text-muted" role="status">
      <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-80" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4Z" />
      </svg>
      <span className="text-sm">{label}…</span>
    </div>
  );
}

export function ErrorNotice({ title = 'Something went wrong', message }: { title?: string; message: string }) {
  return (
    <div className="rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-4" role="alert">
      <p className="text-sm font-semibold text-red-900 dark:text-red-200">{title}</p>
      <p className="mt-1 text-sm text-red-800 dark:text-red-200">{message}</p>
    </div>
  );
}

export function EmptyState({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  return (
    <div className="card p-6 text-center sm:p-10">
      <h3 className="text-base font-semibold text-ink">{title}</h3>
      <p className="mx-auto mt-2 max-w-md text-sm text-muted">{body}</p>
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

export function StatCard({
  label,
  value,
  suffix,
  hint,
  tone = 'default',
}: {
  label: string;
  value: string | number;
  suffix?: string;
  hint?: string;
  tone?: 'default' | 'good' | 'warn' | 'bad';
}) {
  const toneClass = {
    default: 'text-ink',
    good: 'text-emerald-700 dark:text-emerald-300',
    warn: 'text-amber-700 dark:text-amber-300',
    bad: 'text-red-700 dark:text-red-300',
  }[tone];

  return (
    <div className="card p-4 sm:p-5">
      <p className="text-xs font-medium uppercase tracking-wide text-muted">{label}</p>
      <p className={`mt-2 text-3xl font-bold tabular-nums ${toneClass}`}>
        {value}
        {suffix ? <span className="ml-0.5 text-lg font-semibold">{suffix}</span> : null}
      </p>
      {hint ? <p className="mt-2 text-xs leading-relaxed text-muted">{hint}</p> : null}
    </div>
  );
}

/**
 * Small, honest progress bar used for accuracy figures throughout.
 *
 * The two labels claim 120px of a row. On a desktop that is a rounding error;
 * inside a card on a 375px phone it was over half the available width, leaving
 * a bar too short to read a value off. Below `sm` the weighting drops onto its
 * own line and the bar gets the room back.
 */
export function AccuracyBar({ pct, weightPct }: { pct: number; weightPct?: number }) {
  const tone = pct >= 70 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div>
      <div className="flex items-center gap-3">
        <div className="h-2 flex-1 overflow-hidden rounded-full bg-line">
          <div className={`h-full rounded-full ${tone}`} style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
        </div>
        <span className="w-12 shrink-0 text-right text-sm font-semibold tabular-nums text-muted sm:w-14">{pct}%</span>
        {weightPct !== undefined ? (
          <span className="hidden w-16 shrink-0 text-right text-xs text-subtle sm:inline">{weightPct}% of paper</span>
        ) : null}
      </div>
      {weightPct !== undefined ? (
        <p className="mt-1 text-xs text-subtle sm:hidden">{weightPct}% of the real paper</p>
      ) : null}
    </div>
  );
}

export function Callout({ tone = 'info', children }: { tone?: 'info' | 'warn'; children: ReactNode }) {
  const cls =
    tone === 'warn'
      ? 'border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 text-amber-900 dark:text-amber-200'
      : 'border-line bg-raised text-muted';
  return <div className={`rounded-lg border p-4 text-sm leading-relaxed ${cls}`}>{children}</div>;
}

/**
 * Minimal markdown renderer for AI-written reports: headings, bold, italics,
 * bullets, blockquotes and horizontal rules. Deliberately not a full parser —
 * it renders text we generated, and it never injects raw HTML.
 */
function isTableRow(line: string): boolean {
  return line.trim().startsWith('|') && line.trim().endsWith('|');
}

function isTableRule(line: string): boolean {
  return /^\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?$/.test(line.trim());
}

function splitTableRow(line: string): string[] {
  return line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((cell) => cell.trim());
}

/**
 * Supports the same subset of markdown as the API's server-prerendered legal
 * pages (`packages/shared/src/markdown.ts`) — headings, lists, blockquotes,
 * horizontal rules, pipe tables, and inline bold/italic/code/links. Keep the
 * two in sync: a construct only one of them understands renders wrong for
 * whichever renderer sees it second.
 */
export function Markdown({ content }: { content: string }) {
  const lines = content.split('\n');
  const out: ReactNode[] = [];
  let listBuffer: string[] = [];

  const flushList = () => {
    if (listBuffer.length === 0) return;
    out.push(
      <ul key={`ul-${out.length}`} className="my-3 list-disc space-y-1.5 pl-5">
        {listBuffer.map((item, i) => (
          <li key={i} className="text-sm leading-relaxed text-muted">
            {inline(item)}
          </li>
        ))}
      </ul>,
    );
    listBuffer = [];
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!.trimEnd();
    if (/^\s*[-*]\s+/.test(line)) {
      listBuffer.push(line.replace(/^\s*[-*]\s+/, ''));
      continue;
    }
    flushList();

    if (line.trim() === '') continue;

    // A table is a header row immediately followed by its `---|---` rule.
    if (isTableRow(line) && lines[i + 1] !== undefined && isTableRule(lines[i + 1]!)) {
      const header = splitTableRow(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && isTableRow(lines[i]!)) {
        rows.push(splitTableRow(lines[i]!));
        i++;
      }
      i--; // the for-loop's own increment accounts for the row just consumed
      out.push(
        <div key={out.length} className="my-3 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                {header.map((cell, c) => (
                  <th key={c} className="border-b border-line py-1.5 pr-4 text-left font-semibold text-ink">
                    {inline(cell)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td key={c} className="border-b border-line py-1.5 pr-4 align-top text-muted">
                      {inline(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    if (line.startsWith('---')) {
      out.push(<hr key={out.length} className="my-5 border-line" />);
    } else if (line.startsWith('### ')) {
      out.push(<h4 key={out.length} className="mt-5 text-sm font-semibold text-ink">{inline(line.slice(4))}</h4>);
    } else if (line.startsWith('## ')) {
      out.push(<h3 key={out.length} className="mt-6 text-base font-semibold text-ink">{inline(line.slice(3))}</h3>);
    } else if (line.startsWith('# ')) {
      out.push(<h2 key={out.length} className="mt-6 text-lg font-bold text-ink">{inline(line.slice(2))}</h2>);
    } else if (line.startsWith('> ')) {
      out.push(
        <blockquote key={out.length} className="my-3 border-l-3 border-line bg-raised py-2 pl-4 text-sm italic text-muted">
          {inline(line.slice(2))}
        </blockquote>,
      );
    } else if (/^\d+\.\s/.test(line)) {
      out.push(<p key={out.length} className="my-1.5 text-sm leading-relaxed text-muted">{inline(line)}</p>);
    } else {
      out.push(<p key={out.length} className="my-2.5 text-sm leading-relaxed text-muted">{inline(line)}</p>);
    }
  }
  flushList();

  return <div>{out}</div>;
}

/** Handles **bold**, *italic*, `code` and [text](url) links within a line. */
function inline(text: string): ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*|_[^_]+_|`[^`]+`|\[[^\]]+\]\([^)]+\))/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-semibold text-ink">{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('_') && part.endsWith('_') && part.length > 2) {
      return <em key={i} className="italic text-muted">{part.slice(1, -1)}</em>;
    }
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return <code key={i} className="rounded bg-raised px-1 py-0.5 text-xs">{part.slice(1, -1)}</code>;
    }
    const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      const [, linkText, url] = link;
      const external = /^https?:\/\//.test(url!);
      return (
        <a
          key={i}
          href={url}
          className="font-medium text-brand-700 underline underline-offset-2 dark:text-brand-300"
          {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          {linkText}
        </a>
      );
    }
    return <span key={i}>{part}</span>;
  });
}
